import apiClient from '@shared/services/http/apiClient';
import { getEnv } from '@shared/utils/env';

import { captureScreenshot } from './captureScreenshot';

export type ClientErrorSource = 'cashin' | 'cashout' | 'boundary' | 'request' | 'manual';

export interface ClientErrorReport {
  source: ClientErrorSource;
  kind?: string;
  code?: string;
  httpStatus?: number;
  axiosCode?: string;
  method?: string;
  url?: string;
  correlationId?: string;
  requestId?: string;
  idCashRegister?: number;
  message?: string;
  stack?: string;
  context?: Record<string, unknown>;
  userComment?: string;
}

export interface ReportOptions {
  screenshot?: boolean | Blob | null;
  force?: boolean;
}

const ENDPOINT = 'api/v1/corresponsal/client-errors';
const DEDUPE_WINDOW_MS = 10_000;
const PAINT_FALLBACK_MS = 100;
const CONTEXT_MAX_LENGTH = 4000;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const recentReports = new Map<string, number>();

export const isClientErrorReportingEnabled = (): boolean =>
  getEnv().VITE_ENABLE_CLIENT_ERROR_REPORTING === 'true';

export const resetClientErrorReporter = (): void => {
  recentReports.clear();
};

const clip = (value: string | undefined, max: number): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value.slice(0, max) : undefined;

const withoutQuery = (url: string | undefined): string | undefined => url?.split('?')[0];

const safeContext = (context: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
  if (!context) return undefined;
  try {
    return JSON.stringify(context).length <= CONTEXT_MAX_LENGTH ? context : undefined;
  } catch {
    return undefined;
  }
};

const toInteger = (value: number | undefined): number | undefined =>
  Number.isInteger(value) ? value : undefined;

export const buildClientErrorPayload = (report: ClientErrorReport): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    source: report.source,
    kind: clip(report.kind, 20),
    code: clip(report.code, 100),
    http_status: toInteger(report.httpStatus),
    axios_code: clip(report.axiosCode, 50),
    method: clip(report.method?.toUpperCase(), 10),
    url: clip(withoutQuery(report.url), 500),
    correlation_id: clip(report.correlationId, 100),
    request_id: report.requestId && UUID_REGEX.test(report.requestId) ? report.requestId : undefined,
    id_cash_register: toInteger(report.idCashRegister),
    message: clip(report.message, 1000),
    stack: clip(report.stack, 8000),
    route: clip(window.location.pathname, 300),
    app_version: clip(getEnv().VITE_APP_BUILD_TIME, 100),
    user_agent: clip(navigator.userAgent, 500),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    context: safeContext(report.context),
    user_comment: clip(report.userComment, 1000),
  };
  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined));
};

const dedupeKey = (report: ClientErrorReport): string =>
  [report.source, report.kind, report.code, withoutQuery(report.url), report.correlationId, report.message].join('|');

const isRecentDuplicate = (key: string, now: number): boolean => {
  recentReports.forEach((at, existing) => {
    if (now - at > DEDUPE_WINDOW_MS) recentReports.delete(existing);
  });
  if (recentReports.has(key)) return true;
  recentReports.set(key, now);
  return false;
};

const waitForPaint = (): Promise<void> =>
  new Promise((resolve) => {
    const fallback = setTimeout(resolve, PAINT_FALLBACK_MS);
    if (typeof window.requestAnimationFrame !== 'function') return;
    window.requestAnimationFrame(() => {
      clearTimeout(fallback);
      setTimeout(resolve, 0);
    });
  });

const resolveScreenshot = (screenshot: ReportOptions['screenshot']): Promise<Blob | null> => {
  if (screenshot instanceof Blob) return Promise.resolve(screenshot);
  if (screenshot === true) return waitForPaint().then(() => captureScreenshot());
  return Promise.resolve(null);
};

const uploadScreenshot = async (eventId: number | string, screenshot: Blob): Promise<void> => {
  const form = new FormData();
  form.append('image', screenshot, 'screenshot.jpg');
  await apiClient.post(`${ENDPOINT}/${eventId}/screenshot`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const reportClientError = async (
  report: ClientErrorReport,
  options: ReportOptions = {}
): Promise<number | string | null> => {
  if (!isClientErrorReportingEnabled()) return null;

  try {
    if (!options.force && isRecentDuplicate(dedupeKey(report), Date.now())) return null;

    const screenshotPromise = resolveScreenshot(options.screenshot);

    const response = await apiClient.post(ENDPOINT, buildClientErrorPayload(report));
    const eventId = (response?.data as { data?: { id?: number | string } } | undefined)?.data?.id ?? null;

    const screenshot = await screenshotPromise;
    if (eventId !== null && screenshot) {
      await uploadScreenshot(eventId, screenshot);
    }
    return eventId;
  } catch (error) {
    console.warn('[telemetry] client error report failed', error);
    return null;
  }
};
